import { AfterViewInit, ChangeDetectionStrategy, Component, input, signal, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CurrentUserService } from '@core/services';
import { MenuProfileComponent } from '@layout/components/menu-profile/menu-profile.component';
import { MenuServicesComponent } from '@layout/components/menu-services/menu-services.component';
import { MenuSupportComponent } from '@layout/components/menu-support/menu-support.component';
import { NewOrderButtonComponent } from '@layout/components/new-order-button/new-order-button.component';
import { UserAvatarComponent } from '@layout/components/user-avatar.component';
import { LayoutService } from '@layout/core/services';
import { TranslateModule } from '@ngx-translate/core';
import { AppRoutes } from '@shared/enums';
import { AccessibilityHelper, GeneralHelper } from '@shared/helpers';
import { CurrentUser } from '@shared/interfaces';
import { ButtonDirective } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';

@Component({
    selector: 'app-toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    imports: [
        ButtonDirective,
        TranslateModule,
        PopoverModule,
        MenuServicesComponent,
        MenuSupportComponent,
        MenuProfileComponent,
        UserAvatarComponent,
        RouterLink,
        NewOrderButtonComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToolbarComponent implements AfterViewInit {
    hideUserInfo = input<boolean>(false);

    currentUser = signal<CurrentUser | null>(this.currentUserService.getCurrentUser());
    userShortName = signal<string | null>(
        GeneralHelper.computeUserShortName(this.currentUser()?.firstName ?? '', this.currentUser()?.lastName ?? '')
    );
    isSidebarCollapsed = signal<boolean>(this.layoutService.isSidebarCollapsed$.value);
    isOnImpersonation = signal<boolean>(!!this.currentUserService.getImpersonatedUser());

    menuProfileOpened = signal<boolean>(false);
    menuSupportOpened = signal<boolean>(false);
    menuServicesOpened = signal<boolean>(false);

    servicesMenu = viewChild<Popover | null>('servicesMenu');
    supportMenu = viewChild<Popover | null>('supportMenu');
    profileMenu = viewChild<Popover | null>('profileMenu');

    protected readonly urls = AppRoutes;

    constructor(
        private currentUserService: CurrentUserService,
        private layoutService: LayoutService
    ) {
        this.currentUserService.currentUser$.subscribe(user => {
            this.currentUser.set(user);
            this.userShortName.set(GeneralHelper.computeUserShortName(user.firstName, user.lastName));
        });

        this.layoutService.isSidebarCollapsed$.subscribe(isCollapsed => {
            this.isSidebarCollapsed.set(isCollapsed);
        });

        this.currentUserService.impersonatedUser$.subscribe(user => {
            this.isOnImpersonation.set(!!user);
        });
    }

    ngAfterViewInit(): void {
        if (this.servicesMenu()) {
            this.servicesMenu()?.onShow.subscribe(() => {
                AccessibilityHelper.fixOverlayPanelAccessibility(this.servicesMenu()!);
            });
        }
        if (this.supportMenu()) {
            this.supportMenu()?.onShow.subscribe(() => {
                AccessibilityHelper.fixOverlayPanelAccessibility(this.supportMenu()!);
            });
        }
        if (this.profileMenu()) {
            this.profileMenu()?.onShow.subscribe(() => {
                AccessibilityHelper.fixOverlayPanelAccessibility(this.profileMenu()!);
            });
        }
    }

    stopImpersonation(): void {
        this.currentUserService.removeUserToImpersonate();
    }
}
